import { FC, useEffect } from "react";

import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { CalendarIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectSortedTerms, selectSortedTermsForCurrentUser } from "store/selectors";
import { trackEvent } from "utils/amplitude";

export type TermMenuProps = {
  selectedTermId: string | null;
  onTermChange: (termId: string) => void;
  showIcons?: boolean;
  size?: "xs" | "s";
  defaultToAll?: boolean;
  showEmptyOption?: boolean;
  eventPrefix?: string;
  filterToCurrentUser?: boolean;
  hideIfNoTerms?: boolean;
  className?: string;
};

export const TermMenu: FC<TermMenuProps> = ({
  selectedTermId,
  onTermChange,
  showIcons = true,
  defaultToAll = true,
  showEmptyOption = true,
  size = "xs",
  eventPrefix = "",
  filterToCurrentUser = false,
  hideIfNoTerms = false,
  className,
}) => {
  const allTerms = useAppSelector(selectSortedTerms);
  const termsForCurrentUser = useAppSelector(selectSortedTermsForCurrentUser);
  const terms = filterToCurrentUser ? termsForCurrentUser : allTerms;
  const defaultText = defaultToAll ? t("import_modal.all_terms") : t("import_modal.select_term");
  const termOptions = showEmptyOption ? [{ id: "", title: defaultText }, ...terms] : terms;

  useEffect(() => {
    storeApi.terms.list();
  }, []);

  if (hideIfNoTerms && terms.length === 0) {
    return null;
  }

  const listboxItems = termOptions.map((termOption) => ({
    id: termOption.id,
    title: termOption.title,
    iconStart: showIcons ? <CalendarIcon /> : undefined,
  }));

  return (
    <Listbox
      buttonSize={size}
      className={className}
      items={listboxItems}
      selectedValue={selectedTermId ?? ""}
      onSelectionChange={(item) => {
        trackEvent(`${eventPrefix}Term selected`, {
          eventCategory: "Button press",
          term: item.title,
        });
        onTermChange(item.id);
      }}
    />
  );
};
